/**
 * Turn a step's real output (from a builder test run) into dot-paths the data
 * picker can offer — e.g. node_results.node_3.output.results.0.stock_qyt — so
 * people pick fields that actually exist instead of guessing names.
 *
 * Paths use ".0." for the first list element, which the backend resolves
 * (resolve_path in catax_ai/workflow/dag_topology.py).
 */

export interface SampleField {
  path: string;     // relative to the value passed in (no leading dot)
  type: string;     // string | number | boolean | null | array | object
  preview: string;  // short example value
}

const MAX_FIELDS = 300;
const MAX_DEPTH = 6;

function typeOf(v: unknown): string {
  if (v === null || v === undefined) return 'null';
  if (Array.isArray(v)) return 'array';
  return typeof v === 'object' ? 'object' : typeof v;
}

function preview(v: unknown): string {
  if (Array.isArray(v)) return `${v.length} item${v.length === 1 ? '' : 's'}`;
  if (v && typeof v === 'object') return `${Object.keys(v as object).length} fields`;
  const s = v === null || v === undefined ? 'null' : String(v);
  return s.length > 40 ? `${s.slice(0, 40)}…` : s;
}

/** Every path inside value (objects and the first element of lists), depth-limited. */
export function flattenSample(value: unknown, prefix = '', depth = 0, out: SampleField[] = []): SampleField[] {
  if (out.length >= MAX_FIELDS || depth > MAX_DEPTH) return out;
  if (Array.isArray(value)) {
    if (value.length > 0 && value[0] !== null && typeof value[0] === 'object') {
      flattenSample(value[0], prefix ? `${prefix}.0` : '0', depth + 1, out);
    }
    return out;
  }
  if (value && typeof value === 'object') {
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (out.length >= MAX_FIELDS) break;
      if (!/^[A-Za-z0-9_-]+$/.test(k)) continue;   // keys a dot-path can't address
      const path = prefix ? `${prefix}.${k}` : k;
      out.push({ path, type: typeOf(v), preview: preview(v) });
      if (v && typeof v === 'object') flattenSample(v, path, depth + 1, out);
    }
  }
  return out;
}
